import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Space, DatePicker, Button, Spin, message, Input, Badge, Popover, Radio, Typography, Modal } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getStockEvolution } from '../../core/api/mediaplan';
import { useSelector } from 'react-redux';
import StockEvolutionDetails from './product-components/StockEvolutionDetails';

const { RangePicker } = DatePicker;
const { Title, Paragraph, Text } = Typography;

// Component for rotated date headers
const RotatedHeaderCell = ({ date }) => {
  return (
    <div className="rotated-header-container">
      <div className="rotated-header-content">
        {date}
      </div>
    </div>
  );
};

const ProductStockEvolution = () => {
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(3, 'month'));
  const [targetDate, setTargetDate] = useState(dayjs().add(90, 'days')); // Default target date is 90 days from today
  const [stockData, setStockData] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [excludeDelivery, setExcludeDelivery] = useState(true);
  const searchInput = useRef(null);
  
  // State for delivery details
  const [selectedDeliveries, setSelectedDeliveries] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  
  // Function to prepare delivery details for popover
  const prepareDeliveryDetails = (productName, variationName, date, orders) => {
    setSelectedProduct(`${productName} - ${variationName}`);
    setSelectedDate(dayjs(date).format('YYYY-MM-DD'));
    setSelectedDeliveries(orders);
    return orders;
  };
  
  // Get selected clients from Redux store
  const selectedClients = useSelector((state) => state.selectedClients || []);

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
    }
  };
  
  // Fetch data when component mounts or when selectedClients changes
  useEffect(() => {
    if (selectedClients.length > 0) {
      fetchStockEvolution();
    }
  }, [selectedClients]);

  // Fetch stock evolution data
  const fetchStockEvolution = async () => {
    if (selectedClients.length === 0) return;
    
    setLoading(true);
    try {
      const response = await getStockEvolution(startDate, endDate, selectedClients, excludeDelivery, targetDate);
      setStockData(response.data);
      processStockData(response.data);
    } catch (error) {
      console.error('Error fetching stock evolution data:', error);
      message.error('Failed to fetch stock evolution data');
    } finally {
      setLoading(false);
    }
  };

  // Handle search functionality with real-time filtering
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedKeys(value ? [value] : []);
            // Auto-confirm on each keystroke for real-time filtering
            confirm({ closeDropdown: false });
            setSearchText(value);
            setSearchedColumn(dataIndex);
          }}
          style={{ width: 188, marginBottom: 0, display: 'block' }}
          autoFocus
        />
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span style={{ fontWeight: searchText && text.includes(searchText) ? 'bold' : 'normal' }}>
          {text}
        </span>
      ) : (
        text
      ),
  });

  // Process stock data to create table columns and data source
  const processStockData = (data) => {
    if (!data || !data.products) return;

    // Create date columns
    const dateColumns = [];
    const startDateObj = dayjs(data.start_date);
    const endDateObj = dayjs(data.end_date);
    let currentDate = startDateObj;

    // Fixed columns for index and product
    const fixedColumns = [
      {
        title: '#',
        dataIndex: 'index',
        key: 'index',
        width: 30,
        fixed: 'left',
      },
      {
        title: 'Product',
        dataIndex: 'product_name',
        key: 'product_name',
        width: 200,
        fixed: 'left',
        ellipsis: true,
        ...getColumnSearchProps('product_name'),
      },
      {
        title: 'Product Description',
        dataIndex: 'variation_name',
        key: 'variation_name',
        width: 200,
        ellipsis: true,
        ...getColumnSearchProps('variation_name'),
      },
      {
        title: 'Current Stock',
        dataIndex: 'initial_stock',
        key: 'initial_stock',
        width: 100,
        fixed: 'left',
        align: 'center',
        sorter: (a, b) => a.initial_stock - b.initial_stock,
        sortDirections: ['descend', 'ascend'],
        render: (value) => {
          let color = 'black';
          if (value < 0) {
            color = 'red';
          } else if (value < 100) {
            color = 'orange';
          }
          return <span style={{ color }}>{value}</span>;
        },
      },
      {
        title: 'Last Updated',
        dataIndex: 'last_refresh_date',
        key: 'last_refresh_date',
        width: 100,
        align: 'center',
        render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '-',
      },
      {
        title: 'Stock on Day 60',
        dataIndex: 'stock_day_60',
        key: 'stock_day_60',
        width: 120,
        align: 'center',
        sorter: (a, b) => (a.stock_day_60 || 0) - (b.stock_day_60 || 0),
        sortDirections: ['descend', 'ascend'],
        render: (value) => {
          let color = 'black';
          if (value !== null) {
            if (value < 0) {
              color = 'red';
            } else if (value < 100) {
              color = 'orange';
            }
            return <span style={{ color }}>{Math.round(value)}</span>;
          }
          return '';
        },
      },
      {
        title: 'Quantity to Order',
        dataIndex: 'quantity_to_order',
        key: 'quantity_to_order',
        width: 100,
        align: 'center',
        sorter: (a, b) => (a.quantity_to_order || 0) - (b.quantity_to_order || 0),
        sortDirections: ['descend', 'ascend'],
        render: (value) => value !== null ? Math.round(value) : '',
      },
      {
        title: 'Order Date at Latest',
        dataIndex: 'order_date',
        key: 'order_date',
        width: 100,
        align: 'center',
        render: (text) => text || '',
      },
      {
        title: 'Order Priority',
        dataIndex: 'order_priority',
        key: 'order_priority',
        width: 70,
        align: 'center',
        sorter: {
          compare: (a, b) => {
            const aEmpty = a.order_priority == null || a.order_priority === '';
            const bEmpty = b.order_priority == null || b.order_priority === '';
            if (aEmpty && !bEmpty) return 1;
            if (!aEmpty && bEmpty) return -1;
            if (aEmpty && bEmpty) return 0;
            return a.order_priority - b.order_priority;
          },
          multiple: 1
        },
        sortDirections: ['descend', 'ascend'],
        render: (value) => {
          if (value === null) return '';
      
          const clamp = (num, min, max) => Math.max(min, Math.min(num, max));
      
          // RGB points
          const RED = [226, 4, 4];
          const YELLOW = [237, 189, 2];
          const GREEN = [11, 127, 30];
      
          let r = 237, g = 189, b = 2; // default to yellow
      
          if (value < 0) {
            const ratio = clamp((value + 60) / 60, 0, 1); // -60 to 0
            r = Math.round(RED[0] + ratio * (YELLOW[0] - RED[0]));
            g = Math.round(RED[1] + ratio * (YELLOW[1] - RED[1]));
            b = Math.round(RED[2] + ratio * (YELLOW[2] - RED[2]));
          } else if (value > 0) {
            const ratio = clamp(value / 60, 0, 1); // 0 to 60
            r = Math.round(YELLOW[0] + ratio * (GREEN[0] - YELLOW[0]));
            g = Math.round(YELLOW[1] + ratio * (GREEN[1] - YELLOW[1]));
            b = Math.round(YELLOW[2] + ratio * (GREEN[2] - YELLOW[2]));
          }
      
          const backgroundColor = `rgb(${r}, ${g}, ${b})`;
      
          return (
            <div style={{ 
              backgroundColor, 
              padding: '2px 4px', 
              borderRadius: '4px',
              color: value === 0 ? 'black' : 'white'
            }}>
              {value}
            </div>
          );
        },
      },
      {
        title: 'Delivery Priority',
        dataIndex: 'delivery_priority',
        key: 'delivery_priority',
        width: 74,
        align: 'center',
        sorter: {
          compare: (a, b) => {
            const aEmpty = a.delivery_priority == null || a.delivery_priority === '';
            const bEmpty = b.delivery_priority == null || b.delivery_priority === '';
            if (aEmpty && !bEmpty) return 1;
            if (!aEmpty && bEmpty) return -1;
            if (aEmpty && bEmpty) return 0;
            return a.delivery_priority - b.delivery_priority;
          },
          multiple: 1
        },
        sortDirections: ['descend', 'ascend'],
        render: (value) => {
          if (value === null) return '';
      
          const clamp = (num, min, max) => Math.max(min, Math.min(num, max));
      
          // RGB points
          const RED = [226, 4, 4];
          const YELLOW = [237, 189, 2];
          const GREEN = [11, 127, 30];
      
          let r = 237, g = 189, b = 2; // default to yellow
      
          if (value < 0) {
            const ratio = clamp((value + 60) / 60, 0, 1); // -60 to 0
            r = Math.round(RED[0] + ratio * (YELLOW[0] - RED[0]));
            g = Math.round(RED[1] + ratio * (YELLOW[1] - RED[1]));
            b = Math.round(RED[2] + ratio * (YELLOW[2] - RED[2]));
          } else if (value > 0) {
            const ratio = clamp(value / 60, 0, 1); // 0 to 60
            r = Math.round(YELLOW[0] + ratio * (GREEN[0] - YELLOW[0]));
            g = Math.round(YELLOW[1] + ratio * (GREEN[1] - YELLOW[1]));
            b = Math.round(YELLOW[2] + ratio * (GREEN[2] - YELLOW[2]));
          }
      
          const backgroundColor = `rgb(${r}, ${g}, ${b})`;
      
          return (
            <div style={{ 
              backgroundColor, 
              padding: '2px 4px', 
              borderRadius: '4px',
              color: value === 0 ? 'black' : 'white'
            }}>
              {value}
            </div>
          );
        },
      },
      {
        title: 'Out of Stock Date',
        dataIndex: 'out_of_stock_date',
        key: 'out_of_stock_date',
        width: 100,
        align: 'center',
        render: (text) => text || '',
      },     
      
    ];

    // Generate date columns
    while (currentDate.isBefore(endDateObj) || currentDate.isSame(endDateObj, 'day')) {
      const dateStr = currentDate.format('YYYY-MM-DD');
      const displayDate = currentDate.format('MMM DD, YYYY');
      
      dateColumns.push({
        title: () => <RotatedHeaderCell date={displayDate} />,
        dataIndex: dateStr,
        key: dateStr,
        width: 75,
        align: 'center',
        sorter: (a, b) => {
          const aValue = a[dateStr] !== undefined ? a[dateStr] : -Infinity;
          const bValue = b[dateStr] !== undefined ? b[dateStr] : -Infinity;
          return aValue - bValue;
        },
        sortDirections: ['descend', 'ascend'],
        render: (value, record) => {
          let color = 'black';
          if (value < 0) {
            color = 'red';
          } else if (value < 100) {
            color = 'orange';
          }
          
          // Check if there are deliveries for this date
          const deliveryInfo = record[`${dateStr}_deliveries`];
          
          if (deliveryInfo && deliveryInfo.count > 0) {
            return (
              <Popover
                content={
                  <Card
                    title={`Expected Deliveries for ${record.product_name} on ${dayjs(dateStr).format('YYYY-MM-DD')}`}
                    style={{ width: 700, maxHeight: 500, overflow: 'auto' }}
                    size="small"
                    bordered={false}
                    className='small-font'
                  >
                    <Table
                      dataSource={prepareDeliveryDetails(record.product_name, record.variation_name, dateStr, deliveryInfo.orders).map((order, index) => ({ ...order, key: index }))}
                      columns={[
                        {
                          title: '#',
                          dataIndex: 'key',
                          key: 'key',
                          width: 20,
                          render: (text) => text + 1
                        },
                        {
                          title: 'Invoice Number',
                          dataIndex: 'invoice_number',
                          key: 'invoice_number',
                          width: 120,
                        },
                        {
                          title: 'Supplier',
                          dataIndex: 'supplier',
                          key: 'supplier',
                          width: 120,
                        },
                        {
                          title: 'Order Date',
                          dataIndex: 'order_date',
                          key: 'order_date',
                          width: 90,
                        },
                        {
                          title: 'Expected',
                          dataIndex: 'expected_delivery_date',
                          key: 'expected_delivery_date',
                          width: 90,
                        },
                        {
                          title: 'Amount',
                          dataIndex: 'order_amount',
                          key: 'order_amount',
                          width: 70,
                          align: 'right',
                        },
                        {
                          title: 'Received',
                          dataIndex: 'received',
                          key: 'received',
                          width: 70,
                          align: 'right',
                        },
                        {
                          title: 'Remaining',
                          dataIndex: 'remaining',
                          key: 'remaining',
                          width: 80,
                          align: 'right',
                          render: (value) => <span style={{ color: 'red', fontWeight: 'bold' }}>{value}</span>
                        },
                      ]}
                      pagination={false}
                      size="small"
                      scroll={{ y: 300 }}
                    />
                  </Card>
                }
                trigger="click"
                placement="right"
                overlayStyle={{ maxWidth: '1000px' }}
              >
                <Badge 
                  count={deliveryInfo.count} 
                  style={{ 
                    backgroundColor: '#f5222d',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    fontSize: '8px',
                    padding: '0 2px',
                    position: 'absolute',
                    top: '-3px',
                    right: '-10px',
                    height: '15px',
                    minWidth: '15px',
                    lineHeight: '15px'
                  }}
                >
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <span style={{ color }}>{value !== undefined ? Math.round(value) : '-'}</span>
                  </div>
                </Badge>
              </Popover>
            );
          }
          
          return <span style={{ color }}>{value !== undefined ? Math.round(value) : '-'}</span>;
        },
      });
      currentDate = currentDate.add(1, 'day');
    }

    // Combine fixed and date columns
    const allColumns = [...fixedColumns, ...dateColumns];
    setColumns(allColumns);

    // Create data source from products
    const dataSource = Object.entries(data.products).map(([productKey, productData], index) => {
      const row = {
        key: index,
        index: index + 1,
        product: productKey,
        product_name: productData.product_name,
        variation_name: productData.variation_name,
        variation_id: productData.variation_id,
        initial_stock: productData.initial_stock,
        last_refresh_date: productData.last_refresh_date,
        stock_day_60: productData.stock_day_60,
        quantity_to_order: productData.quantity_to_order,
        order_date: productData.order_date,
        order_priority: productData.order_priority,
        delivery_priority: productData.delivery_priority,
        out_of_stock_date: productData.out_of_stock_date,
      };

      // Add stock values for each date and delivery information if available
      productData.stock_timeline.forEach((item) => {
        row[item.date] = item.stock;
        
        // Add delivery information if available
        if (item.deliveries) {
          row[`${item.date}_deliveries`] = item.deliveries;
        }
      });

      return row;
    });

    setDataSource(dataSource);
  };

  return (
    <Card size="small">
      <div>
        <Space size="large">
          <Space>
            <span>Date Range:</span>
            <RangePicker
              value={[startDate, endDate]}
              onChange={handleDateRangeChange}
              allowClear={false}
            />
          </Space>
          <Radio.Group 
            value={excludeDelivery} 
            onChange={(e) => setExcludeDelivery(e.target.value)}
            style={{ marginRight: '10px' }}
          >
            <Radio value={true}>Exclude Delivery</Radio>
            <Radio value={false}>Include Delivery</Radio>
          </Radio.Group>
          <Space>
            <span>Target Date:</span>
            <DatePicker
              value={targetDate}
              onChange={(date) => setTargetDate(date)}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
              allowClear={false}
              format="YYYY-MM-DD"
            />
          </Space>
          <Button type="primary" onClick={fetchStockEvolution}>
            Apply
          </Button>
          <Button type="link" onClick={() => setIsModalVisible(true)}>
            See Details
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {stockData ? (
          <div style={{ overflowX: 'auto' }} className="small-font">
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              scroll={{ x: 'max-content', y: 600 }}
              size="small"
              bordered
              sticky={{
                offsetHeader: 0,
                offsetScroll: 0,
                getContainer: () => document.querySelector('.stock-evolution-table')
              }}
              tableLayout="fixed"
              style={{ overflowX: 'auto' }}
              className="stock-evolution-table"
            />
            {/* Add CSS styles directly to the component */}
            <style>
              {`
                .stock-evolution-table .ant-table-thead > tr > th {
                  height: 80px;
                  padding: 8px 4px;
                  text-align: center;
                  position: sticky;
                  top: 0;
                  z-index: 2;
                  background: #fafafa;
                  vertical-align: bottom;
                }
                .stock-evolution-table .ant-table-sticky-holder {
                  position: sticky;
                  top: 0;
                  z-index: 3;
                }
                .stock-evolution-table .ant-table-cell {
                  padding: 8px 4px !important;
                }
                .stock-evolution-table .ant-table-cell.ant-table-cell-fix-left {
                  z-index: 100 !important;
                  background: #fff;
                }
                .stock-evolution-table .ant-table-thead .ant-table-cell.ant-table-cell-fix-left {
                  background: #fafafa;
                  z-index: 101 !important;
                }
                .rotated-header-container {
                  height: 70px;
                  width: 100%;
                  position: relative;
                  margin-left: 5px;
                }
                .rotated-header-content {
                  position: absolute;
                  bottom: 0;
                  left: 0;
                  transform: rotate(-45deg);
                  transform-origin: 0% 100%;
                  white-space: nowrap;
                  font-size: 12px;
                  padding-left: 5px;
                }
                .small-font {
                  font-size: 12px;
                }
              `}
            </style>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            {selectedClients.length > 0 ? 
              'Select date range and click Apply to view stock evolution data' : 
              'Please select at least one client from the header to view stock evolution data'}
          </div>
        )}
      </Spin>
      
      {/* Delivery details are now shown in popover overlays */}
      
      <Modal
        title="Stock Evolution Calculation Details" 
        open={isModalVisible} 
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        <StockEvolutionDetails />
      </Modal>
    </Card>
  );
};

export default ProductStockEvolution;
